import { ReactNode } from 'react'

const CONTACT_EMAIL = 'fernando.altuzar@clio.com'
const LAST_UPDATED = 'June 26, 2026'

interface LegalPageProps {
    title: string
    lastUpdated: string
    appName: string
    children: ReactNode
}

/**
 * Reusable, accessible legal/article page. Normal document flow + min-h-screen so
 * the content is always vertically scrollable and never clipped.
 */
export function LegalPage({ title, lastUpdated, appName, children }: LegalPageProps) {
    return (
        <div className="min-h-screen bg-[#FAFAFA] text-slate-900 font-sans selection:bg-slate-900 selection:text-white">
            <div className="mx-auto max-w-3xl px-6 py-10 sm:py-16">
                <div className="mb-10 flex items-center justify-between">
                    <a
                        href="#/"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-[#3D4FE3]"
                    >
                        <span aria-hidden="true">←</span> Back to home
                    </a>
                    <span className="logo-text text-xs text-slate-400">{appName}</span>
                </div>

                <header className="mb-10 border-b border-slate-200 pb-8">
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                        {title}
                    </h1>
                    <p className="mt-3 text-sm font-medium text-slate-500">
                        Last updated: {lastUpdated}
                    </p>
                </header>

                <article className="space-y-8 text-slate-700">
                    {children}
                </article>

                <footer className="mt-16 border-t border-slate-200 pt-8 text-center">
                    <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-medium">
                        <a href="#/" className="text-slate-400 transition-colors hover:text-[#3D4FE3]">
                            Home
                        </a>
                        <a href="#/privacy" className="text-slate-400 transition-colors hover:text-[#3D4FE3]">
                            Privacy
                        </a>
                        <a href="#/terms" className="text-slate-400 transition-colors hover:text-[#3D4FE3]">
                            Terms
                        </a>
                    </nav>
                    <p className="mt-4 text-sm font-medium text-slate-400">
                        © {new Date().getFullYear()} {appName}. All rights reserved.
                    </p>
                </footer>
            </div>
        </div>
    )
}

/** Shared section heading styles for legal copy. */
function H2({ children }: { children: ReactNode }) {
    return <h2 className="text-xl font-bold text-slate-900">{children}</h2>
}

function H3({ children }: { children: ReactNode }) {
    return <h3 className="text-base font-semibold text-slate-900">{children}</h3>
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
    return (
        <section className="space-y-3">
            <H2>{heading}</H2>
            {children}
        </section>
    )
}

function P({ children }: { children: ReactNode }) {
    return <p className="leading-relaxed">{children}</p>
}

function List({ children }: { children: ReactNode }) {
    return <ul className="ml-5 list-disc space-y-2 leading-relaxed marker:text-slate-400">{children}</ul>
}

export function PrivacyPolicy({ appName }: { appName: string }) {
    return (
        <LegalPage title="Privacy Policy" lastUpdated={LAST_UPDATED} appName={appName}>
            <P>
                This Privacy Policy explains how {appName} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
                &ldquo;our&rdquo;) collects, uses, and protects your information when you use our
                mobile and web applications and related services (the &ldquo;Service&rdquo;). We
                built {appName} to be offline-first and privacy-respecting: most of what you read,
                highlight, and track lives on your device first, and only syncs when you choose to
                sign in.
            </P>

            <Section heading="Information we collect">
                <List>
                    <li>
                        <strong>Account information.</strong> When you create an account, we collect
                        your email address (and, if you sign in with Apple or Google, the basic
                        profile identifier they return).
                    </li>
                    <li>
                        <strong>Reading progress and streaks.</strong> Your library, finished
                        summaries, highlights, daily streaks, and preferences are stored locally on
                        your device and synced to your account so you can pick up across devices.
                    </li>
                    <li>
                        <strong>Subscription status.</strong> Your plan and entitlement status are
                        managed through RevenueCat and the Apple App Store or Google Play. We do not
                        receive or store your full payment card details.
                    </li>
                    <li>
                        <strong>AI chat messages.</strong> When you use our AI features (such as
                        chatting about a book), the messages you send are processed by OpenAI to
                        generate a response.
                    </li>
                    <li>
                        <strong>Basic analytics and diagnostics.</strong> We collect aggregate usage
                        and crash data (such as feature usage, app version, and device type) to keep
                        the app stable and improve it.
                    </li>
                </List>
            </Section>

            <Section heading="How we use your information">
                <List>
                    <li>To provide, sync, and maintain your account and library.</li>
                    <li>To track your reading streaks and personalize recommendations.</li>
                    <li>To process subscriptions and verify your access to paid features.</li>
                    <li>To power AI summaries and chat responses.</li>
                    <li>To diagnose problems, prevent abuse, and improve the Service.</li>
                    <li>To communicate with you about important account or service updates.</li>
                </List>
            </Section>

            <Section heading="Third parties and subprocessors">
                <P>
                    We rely on a small set of trusted providers to operate the Service. They process
                    data only on our behalf and under their own privacy and security commitments:
                </P>
                <List>
                    <li>
                        <strong>Convex</strong> — backend database and account sync.
                    </li>
                    <li>
                        <strong>RevenueCat</strong> — subscription and entitlement management.
                    </li>
                    <li>
                        <strong>OpenAI</strong> — AI summary and chat processing.
                    </li>
                    <li>
                        <strong>Apple App Store and Google Play</strong> — billing and payment
                        processing.
                    </li>
                    <li>
                        <strong>Vercel</strong> — hosting of our website and supporting services.
                    </li>
                </List>
                <P>We do not sell your personal information.</P>
            </Section>

            <Section heading="Offline-first and local storage">
                <P>
                    {appName} is designed to work offline. Your library, reading progress, and
                    highlights are written to local storage on your device so you can read on a
                    plane or in a tunnel. When you are signed in and online, this data syncs to your
                    account. You can clear local data at any time by signing out or removing the app.
                </P>
            </Section>

            <Section heading="Data retention">
                <P>
                    We retain your account information and synced reading data for as long as your
                    account is active. If you delete your account, we delete or anonymize your
                    personal data within a reasonable period, except where we must retain it to
                    comply with legal, tax, or fraud-prevention obligations. Aggregated, de-identified
                    analytics may be retained indefinitely.
                </P>
            </Section>

            <Section heading="Your rights">
                <P>
                    You can access, export, correct, or delete your personal data at any time by
                    contacting us. Depending on where you live, you may have additional rights:
                </P>
                <List>
                    <li>
                        <strong>GDPR (EU/EEA/UK).</strong> Rights to access, rectification, erasure,
                        restriction, portability, and objection, plus the right to lodge a complaint
                        with your supervisory authority.
                    </li>
                    <li>
                        <strong>CCPA/CPRA (California).</strong> Rights to know, delete, correct, and
                        opt out of the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal
                        information. We do not sell your data.
                    </li>
                </List>
                <P>
                    To exercise any of these rights, email us at{' '}
                    <a className="font-medium text-[#3D4FE3] hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                    </a>
                    .
                </P>
            </Section>

            <Section heading="Children's privacy">
                <P>
                    {appName} is not directed to children under 13, and we do not knowingly collect
                    personal information from children under 13. If you believe a child has provided
                    us with personal information, please contact us and we will delete it.
                </P>
            </Section>

            <Section heading="Security">
                <P>
                    We use industry-standard safeguards — including encryption in transit and access
                    controls — to protect your information. No method of transmission or storage is
                    100% secure, but we work to protect your data and to notify you of material
                    incidents where required by law.
                </P>
            </Section>

            <Section heading="International data transfers">
                <P>
                    We and our subprocessors may process and store data in countries other than your
                    own, including the United States. Where required, we rely on appropriate
                    safeguards (such as Standard Contractual Clauses) for these transfers.
                </P>
            </Section>

            <Section heading="Changes to this policy">
                <P>
                    We may update this Privacy Policy from time to time. When we make material
                    changes, we will update the &ldquo;Last updated&rdquo; date above and, where
                    appropriate, notify you in the app.
                </P>
            </Section>

            <Section heading="Contact us">
                <P>
                    If you have questions about this Privacy Policy or your data, contact us at{' '}
                    <a className="font-medium text-[#3D4FE3] hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                    </a>
                    .
                </P>
            </Section>
        </LegalPage>
    )
}

export function TermsOfService({ appName }: { appName: string }) {
    return (
        <LegalPage title="Terms of Service" lastUpdated={LAST_UPDATED} appName={appName}>
            <P>
                These Terms of Service (&ldquo;Terms&rdquo;) govern your use of {appName} and our
                related applications and services (the &ldquo;Service&rdquo;). By creating an account
                or using the Service, you agree to these Terms. If you do not agree, please do not
                use the Service.
            </P>

            <Section heading="1. Acceptance of terms">
                <P>
                    By accessing or using {appName}, you confirm that you can form a binding contract
                    with us and that you accept these Terms and our Privacy Policy.
                </P>
            </Section>

            <Section heading="2. Description of the service">
                <P>
                    {appName} provides condensed summaries of nonfiction and other books, available
                    to read or listen to, along with AI-assisted features such
                    as chat. Content is offered for personal, educational use.
                </P>
            </Section>

            <Section heading="3. Accounts">
                <P>
                    You are responsible for maintaining the confidentiality of your account
                    credentials and for all activity under your account. Notify us promptly of any
                    unauthorized use. You must provide accurate information and keep it up to date.
                </P>
            </Section>

            <Section heading="4. Subscriptions and billing">
                <P>{appName} offers the following subscription options:</P>
                <List>
                    <li>Weekly — $4.99</li>
                    <li>Monthly — $9.99</li>
                    <li>Yearly — $59.99, with a 3-day free trial for new subscribers</li>
                    <li>Lifetime — $129.99, one-time purchase</li>
                </List>
                <H3>Auto-renewal</H3>
                <P>
                    Except for the lifetime option, subscriptions automatically renew at the end of
                    each billing period unless you cancel at least 24 hours before the period ends.
                    Your account will be charged for renewal within 24 hours prior to the end of the
                    current period.
                </P>
                <H3>Management and cancellation</H3>
                <P>
                    Purchases are processed and managed through the Apple App Store, Google Play, and
                    RevenueCat. You can view, manage, or cancel your subscription in your App Store or
                    Play Store account settings. If you cancel, you keep access until the end of the
                    current paid period.
                </P>
                <H3>Free trial</H3>
                <P>
                    If you start a 3-day free trial, you will not be charged during the trial. If you
                    do not cancel before the trial ends, your subscription begins and you will be
                    charged automatically. Any unused trial portion is forfeited when you purchase a
                    subscription.
                </P>
                <H3>Refunds</H3>
                <P>
                    Refunds are handled according to the policies of the store through which you
                    purchased (Apple App Store or Google Play). Please direct refund requests to the
                    applicable store.
                </P>
            </Section>

            <Section heading="5. Acceptable use">
                <P>You agree not to:</P>
                <List>
                    <li>Copy, redistribute, resell, or publicly share our content.</li>
                    <li>Scrape, reverse engineer, or attempt to extract our content in bulk.</li>
                    <li>Use the Service to violate any law or infringe the rights of others.</li>
                    <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                    <li>Share your account credentials or access in violation of these Terms.</li>
                </List>
            </Section>

            <Section heading="6. Intellectual property">
                <P>
                    The book summaries available in {appName} are original, condensed works created by
                    or licensed to us; they are not copies of the underlying books. All content,
                    software, trademarks, and design are owned by us or our licensors and are
                    protected by intellectual property laws. We grant you a limited, personal,
                    non-transferable, non-exclusive license to access the content for your own use.
                    Redistribution is prohibited.
                </P>
            </Section>

            <Section heading="7. AI features disclaimer">
                <P>
                    Our summaries and AI chat features are generated with the assistance of automated
                    systems and may be incomplete, inaccurate, or out of date. They are provided for
                    general informational and educational purposes only and do not constitute
                    professional, legal, medical, financial, or other advice. Always consult a
                    qualified professional and the original work before relying on any information.
                </P>
            </Section>

            <Section heading="8. Disclaimers and limitation of liability">
                <P>
                    The Service is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo;
                    without warranties of any kind, whether express or implied. To the maximum extent
                    permitted by law, we are not liable for any indirect, incidental, special,
                    consequential, or punitive damages, or for any loss of data, profits, or goodwill
                    arising from your use of the Service. Our total liability for any claim is limited
                    to the amount you paid us in the 12 months before the claim.
                </P>
            </Section>

            <Section heading="9. Termination">
                <P>
                    We may suspend or terminate your access to the Service if you violate these Terms
                    or use the Service in a way that could harm us or other users. You may stop using
                    the Service and delete your account at any time.
                </P>
            </Section>

            <Section heading="10. Governing law">
                <P>
                    These Terms are governed by the laws of the jurisdiction in which we are
                    established, without regard to conflict-of-law principles. Any disputes will be
                    resolved in the courts of that jurisdiction, unless applicable law requires
                    otherwise.
                </P>
            </Section>

            <Section heading="11. Changes to these terms">
                <P>
                    We may update these Terms from time to time. When we make material changes, we
                    will update the &ldquo;Last updated&rdquo; date above and, where appropriate,
                    notify you in the app. Continued use of the Service after changes take effect
                    means you accept the updated Terms.
                </P>
            </Section>

            <Section heading="12. Contact us">
                <P>
                    Questions about these Terms? Contact us at{' '}
                    <a className="font-medium text-[#3D4FE3] hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
                        {CONTACT_EMAIL}
                    </a>
                    .
                </P>
            </Section>
        </LegalPage>
    )
}
